
import React, { useState, useEffect, useRef } from 'react';
import { 
  ScrollText, 
  ArrowLeft, 
  ShieldCheck, 
  User, 
  Mail, 
  Key, 
  ChevronRight,
  LogIn,
  Shield,
  Compass,
  Fingerprint,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  Lock,
  Zap,
  History,
  ShieldAlert,
  ArrowRight,
  Cpu,
  RefreshCw,
  Activity
} from 'lucide-react';
import { UserProfile, UserRole } from '../types';
import { authService, GOOGLE_AUTH_SECRET } from '../services/authService';

interface LoginPageProps {
  onLogin: (user: UserProfile) => void;
  initialMode?: 'login' | 'signup';
  onBackToHome?: () => void;
}

type AuthState = 'idle' | 'verifying' | 'granted' | 'denied';

declare const google: any;

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin, initialMode = 'login', onBackToHome }) => {
  const [isSignup, setIsSignup] = useState(initialMode === 'signup');
  const [selectedRole, setSelectedRole] = useState<UserRole>('owner');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authState, setAuthState] = useState<AuthState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loadingStep, setLoadingStep] = useState<string>('');
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof google !== 'undefined') {
      google.accounts.id.initialize({
        client_id: "130693704402-v566b60ukc3db2n0rlpiboqt0pqacd4e.apps.googleusercontent.com",
        callback: handleGoogleResponse,
      });

      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = '';
        google.accounts.id.renderButton(googleButtonRef.current, {
          theme: "outline",
          size: "large", 
          width: 340, 
          shape: "pill",
          text: isSignup ? "signup_with" : "signin_with",
          logo_alignment: "left"
        });
      }
    }
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    }
  }, [isSignup, selectedRole]);

  const startAuthTimeout = () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      if (authState === 'verifying') {
        setAuthState('denied');
        setErrorMessage("Authentication timed out. Please check your connection.");
      }
    }, 15000); // 15 second safety timeout
  };

  const handleGoogleResponse = async (response: any) => {
    setAuthState('verifying');
    setLoadingStep('Synchronizing Identity...');
    startAuthTimeout();
    try {
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      const socialSecret = GOOGLE_AUTH_SECRET + payload.sub;
      
      await authService.register(payload.name, payload.email, socialSecret, selectedRole);
      
      const loginResult = await authService.login(payload.email, socialSecret);
      if (loginResult.success && loginResult.user) {
        setAuthState('granted');
        setTimeout(() => onLogin(loginResult.user!), 800);
      } else {
        throw new Error(loginResult.message);
      }
    } catch (e: any) {
      setAuthState('denied');
      setErrorMessage(e.message || "Google Authentication protocol failed.");
    } finally {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    if (isSignup && password !== confirmPassword) {
      setErrorMessage("Security passcodes do not match.");
      return;
    }
    setAuthState('verifying');
    setLoadingStep(isSignup ? 'Initializing Vault...' : 'Unlocking Identity...');
    startAuthTimeout();

    try {
      if (isSignup) {
        const regResult = await authService.register(name, email, password, selectedRole);
        if (regResult.success) {
          const loginResult = await authService.login(email, password);
          if (loginResult.success && loginResult.user) {
            setAuthState('granted');
            setTimeout(() => onLogin(loginResult.user!), 500);
          } else {
            setAuthState('denied');
            setErrorMessage(loginResult.message || 'Login failed after registration.');
          }
        } else {
          setAuthState('denied');
          setErrorMessage(regResult.message || 'Registration failed.');
        }
      } else {
        const loginResult = await authService.login(email, password);
        if (loginResult.success && loginResult.user) {
          setAuthState('granted');
          onLogin(loginResult.user);
        } else {
          setAuthState('denied');
          setErrorMessage(loginResult.message || 'Identity verification failed.');
        }
      }
    } catch (err) {
      setAuthState('denied');
      setErrorMessage("System error during authentication.");
    } finally {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    }
  };

  const isOwner = selectedRole === 'owner';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white overflow-hidden selection:bg-indigo-100">
      
      {/* SIDE PANE: Narrative & Branding */}
      <div className={`relative hidden lg:flex lg:w-[45%] flex-col justify-between p-16 transition-all duration-1000 ${isOwner ? 'bg-slate-900' : 'bg-teal-950'}`}>
        <div className="absolute inset-0 opacity-10 safety-mesh">
           <svg width="100%" height="100%"><pattern id="grid-log" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" /></pattern><rect width="100%" height="100%" fill="url(#grid-log)" /></svg>
        </div>
        <div className={`absolute -top-20 -left-20 w-96 h-96 blur-[120px] rounded-full transition-colors duration-1000 ${isOwner ? 'bg-indigo-600/30' : 'bg-teal-500/30'}`}></div>

        <div className="relative z-10">
          <button onClick={onBackToHome} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-white/40 hover:text-white transition-colors mb-20">
            <ArrowLeft size={16} /> Return to Homepage or Mainpage
          </button>
          
          <div className="mb-2">
            <h1 className="text-4xl font-black text-white tracking-tighter">
              Nominee<span className={isOwner ? 'text-indigo-400' : 'text-teal-400'}>Desk</span>
            </h1>
          </div>
        </div>

        <div className="relative z-10 max-w-md animate-fade-in" key={selectedRole}>
          <h2 className="text-5xl font-black text-white leading-[1.1] tracking-tighter mb-8">
            {isOwner ? (
              <>Secure your <span className="text-indigo-400">Digital Estate</span> with precision.</>
            ) : (
              <>Access your <span className="text-teal-400">Heritage Records</span> with clarity.</>
            )}
          </h2>
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border border-white/10 bg-white/5 ${isOwner ? 'text-indigo-400' : 'text-teal-400'}`}>
                {isOwner ? <Shield size={20} /> : <Compass size={20} />}
              </div>
              <p className="text-white/60 font-medium leading-relaxed">
                {isOwner 
                  ? "Map every financial account to its rightful successor. Eliminate legal ambiguity and ensure your intent is executed." 
                  : "Discover exactly what has been left for you. We provide the institutional roadmap for smooth inheritance claims."
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN PANE: Forms & Immersive Loader */}
      <main className="flex-1 flex flex-col justify-center items-center p-8 lg:p-24 bg-graph-paper relative overflow-hidden">
        <div className="w-full max-w-md animate-fade-in">
          
          {authState === 'verifying' ? (
            <div className="flex flex-col items-center justify-center text-center animate-fade-in">
              <div className="relative mb-16 h-48 w-48 flex items-center justify-center">
                 <div className={`absolute inset-0 rounded-full blur-3xl opacity-20 animate-pulse ${isOwner ? 'bg-indigo-500' : 'bg-teal-500'}`}></div>
                 <div className={`absolute inset-0 rounded-full border-[1.5px] border-dashed opacity-40 animate-[spin_8s_linear_infinite] ${isOwner ? 'border-indigo-600' : 'border-teal-600'}`}></div>
                 <div className="absolute inset-4 rounded-full border-[1px] border-slate-100 animate-[spin_4s_linear_infinite]">
                    <div className={`absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full ${isOwner ? 'bg-indigo-600' : 'bg-teal-600'}`}></div>
                 </div>
                 <div className={`relative h-28 w-28 rounded-[2rem] bg-white shadow-2xl flex items-center justify-center border border-slate-50 transition-all duration-500`}>
                    <div className={`absolute inset-0 rounded-[2rem] animate-ping opacity-20 ${isOwner ? 'bg-indigo-600' : 'bg-teal-600'}`}></div>
                    <Lock size={40} className={`transition-all duration-500 ${isOwner ? 'text-indigo-600' : 'text-teal-600'}`} />
                 </div>
                 <div className={`absolute -bottom-2 right-4 p-2 rounded-xl bg-white shadow-lg border border-slate-50 animate-bounce ${isOwner ? 'text-indigo-600' : 'text-teal-600'}`}>
                    <ShieldCheck size={16} />
                 </div>
              </div>

              <div className="space-y-4">
                 <h2 className="text-2xl font-black text-slate-900 tracking-tighter uppercase">{loadingStep}</h2>
                 <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">
                       <Cpu size={12} className="animate-pulse" /> Decrypting Secure Assets
                    </div>
                 </div>
                 <div className="mt-10 px-8 py-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-center gap-4">
                    <Activity size={14} className={isOwner ? 'text-indigo-500' : 'text-teal-500'} />
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                       Hardware-Isolated Keys: <span className="text-emerald-500">Secure</span>
                    </span>
                 </div>
              </div>
            </div>
          ) : (
            <div className="animate-fade-in">
              <div className="mb-12 text-center lg:text-left">
                <h3 className="text-4xl font-black text-slate-900 tracking-tighter mb-2">
                  {isSignup ? 'Initialize Vault' : 'Welcome Back'}
                </h3>
                <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">Identify yourself to continue</p>
              </div>

              <div className="flex bg-slate-100/50 p-1.5 rounded-[2rem] border border-slate-100 mb-10">
                <button onClick={() => setSelectedRole('owner')} className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-[1.75rem] text-[10px] font-black uppercase tracking-widest transition-all ${isOwner ? 'bg-white text-indigo-600 shadow-xl' : 'text-slate-400 hover:text-slate-600'}`}>
                  <Shield size={16} /> Asset Nominator
                </button>
                <button onClick={() => setSelectedRole('nominee')} className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-[1.75rem] text-[10px] font-black uppercase tracking-widest transition-all ${!isOwner ? 'bg-white text-teal-600 shadow-xl' : 'text-slate-400 hover:text-slate-600'}`}>
                  <Compass size={16} /> Beneficiary
                </button>
              </div>

              <div className="space-y-10">
                <div className="flex flex-col items-center gap-8">
                  <div ref={googleButtonRef} className="w-full flex justify-center hover:scale-[1.02] transition-transform"></div>
                  <div className="flex items-center gap-4 w-full px-2">
                    <div className="flex-1 h-px bg-slate-200/50"></div>
                    <span className="text-[9px] font-black text-slate-300 uppercase tracking-[0.5em]">Or Use Email</span>
                    <div className="flex-1 h-px bg-slate-200/50"></div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {isSignup && (
                    <div className="relative group">
                      <User className={`absolute left-5 top-1/2 -translate-y-1/2 transition-colors ${isOwner ? 'group-focus-within:text-indigo-600' : 'group-focus-within:text-teal-600'} text-slate-300`} size={18} />
                      <input required type="text" value={name} onChange={(e) => setName(e.target.value)} className={`w-full pl-14 pr-6 py-5 bg-white border-2 border-slate-100 rounded-2xl focus:border-${isOwner ? 'indigo' : 'teal'}-600 outline-none text-sm font-bold transition-all shadow-sm`} placeholder="Full Legal Name" />
                    </div>
                  )}
                  
                  <div className="relative group">
                    <Mail className={`absolute left-5 top-1/2 -translate-y-1/2 transition-colors ${isOwner ? 'group-focus-within:text-indigo-600' : 'group-focus-within:text-teal-600'} text-slate-300`} size={18} />
                    <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`w-full pl-14 pr-6 py-5 bg-white border-2 border-slate-100 rounded-2xl focus:border-${isOwner ? 'indigo' : 'teal'}-600 outline-none text-sm font-bold transition-all shadow-sm`} placeholder="Email Address" />
                  </div>

                  <div className="relative group">
                    <Key className={`absolute left-5 top-1/2 -translate-y-1/2 transition-colors ${isOwner ? 'group-focus-within:text-indigo-600' : 'group-focus-within:text-teal-600'} text-slate-300`} size={18} />
                    <input required type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className={`w-full pl-14 pr-16 py-5 bg-white border-2 border-slate-100 rounded-2xl focus:border-${isOwner ? 'indigo' : 'teal'}-600 outline-none text-sm font-bold transition-all shadow-sm`} placeholder="Password/ Passcode" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600 transition-colors">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>

                  {isSignup && (
                    <div className="relative group">
                      <ShieldCheck className={`absolute left-5 top-1/2 -translate-y-1/2 transition-colors ${isOwner ? 'group-focus-within:text-indigo-600' : 'group-focus-within:text-teal-600'} text-slate-300`} size={18} />
                      <input required type={showPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={`w-full pl-14 pr-6 py-5 bg-white border-2 border-slate-100 rounded-2xl focus:border-${isOwner ? 'indigo' : 'teal'}-600 outline-none text-sm font-bold transition-all shadow-sm`} placeholder="Confirm Passcode" />
                    </div>
                  )}

                  {errorMessage && (
                    <div className="flex items-center gap-3 p-5 bg-rose-50 text-rose-600 rounded-2xl border border-rose-100 animate-fade-in shadow-sm">
                      <AlertCircle size={18} className="shrink-0" />
                      <span className="text-[10px] font-black uppercase tracking-widest">{errorMessage}</span>
                    </div>
                  )}

                  <button type="submit" className={`w-full group py-5 rounded-2xl text-white font-black text-[11px] uppercase tracking-[0.3em] transition-all duration-500 flex items-center justify-center gap-3 shadow-xl active:scale-[0.98] ${isOwner ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-teal-600 hover:bg-teal-700'}`}>
                    {isSignup ? <Fingerprint size={18} /> : <LogIn size={18} />}
                    {isSignup ? 'Initialize Secure Vault' : 'Unlock Identity Portal'}
                    <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>

                <div className="pt-8 text-center">
                   <button onClick={() => setIsSignup(!isSignup)} className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] hover:text-slate-900 transition-colors">
                     {isSignup ? 'Already have a secure vault? Unlock Access' : 'New to NomineeDesk? Create Identity'}
                   </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
